import React, { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { Box, Spinner } from "@chakra-ui/react";
import { CompactSelect, CompactSelectOption } from "newui/components/ui/InputWithLabel/CompactSelect";
import { CompactTextInput } from "newui/components/ui/InputWithLabel/CompactTextInput";

interface InlineEditableCellBaseProps {
  isSaving?: boolean;
  onSave: (value: string | number) => Promise<void> | void;
}

interface InlineEditableTextCellProps extends InlineEditableCellBaseProps {
  mode: "text";
  testId?: string;
  value: string;
}

interface InlineEditableSelectCellProps extends InlineEditableCellBaseProps {
  mode: "select";
  options: CompactSelectOption[];
  testId?: string;
  value: number | string | null;
}

type InlineEditableCellProps = InlineEditableTextCellProps | InlineEditableSelectCellProps;

export const InlineEditableCell = (props: InlineEditableCellProps) => {
  const { mode, value, onSave, isSaving, testId } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState<string>(String(value ?? ""));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalValue(String(value ?? ""));
  }, [value]);

  const handleSave = async () => {
    setIsEditing(false);
    if (String(value ?? "") !== localValue) {
      await onSave(localValue);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setLocalValue(String(value ?? ""));
      setIsEditing(false);
    }
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  if (mode === "select") {
    const { options } = props as InlineEditableSelectCellProps;

    const handleSelectChange = async (e: ChangeEvent<HTMLSelectElement>) => {
      const raw = e.target.value;
      // Preserve the original value type (number vs string) by looking it up in options.
      const matched = options.find((o) => String(o.value) === raw);
      if (matched) {
        await onSave(matched.value);
      }
    };

    // Size the control to the widest option label so the full category name
    // is always visible. `ch` ~= one character width in the current font;
    // padding (px-2 + pr-6 chevron room) is added on top.
    const longestLabel = options.reduce(
      (m, o) => Math.max(m, String(o.label ?? "").length),
      0
    );
    const widthCh = `${longestLabel + 3}ch`;

    return (
      <Box data-testid={testId} sx={{ width: widthCh }}>
        {isSaving ? (
          <Spinner size="sm" />
        ) : (
          <CompactSelect
            options={options}
            value={value ?? ""}
            onChange={handleSelectChange}
            data-testid={testId}
            // Tighter horizontal padding than the default compact select so
            // the dropdown is no wider than the longest category label.
            selectClassName="!px-2 !pr-6 !py-1"
          />
        )}
      </Box>
    );
  }

  if (isEditing) {
    return (
      <CompactTextInput
        ref={inputRef}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        data-testid={`${testId}-input`}
      />
    );
  }

  return (
    <Box
      cursor="pointer"
      px={2}
      py={1}
      borderRadius="md"
      _hover={{ bg: "gray.100" }}
      onClick={handleStartEdit}
      data-testid={testId}
      minH="32px"
      display="flex"
      alignItems="center"
    >
      {isSaving ? <Spinner size="sm" /> : <span>{localValue || "—"}</span>}
    </Box>
  );
};
